import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { CrudList } from '../shared/CrudPage';
import { ledgerEntryService } from '@kss/api';
import type { LedgerEntry, LedgerEntryCreate, LedgerEntryFilter } from '@kss/api';
import { NameCell } from '../../hooks/useNameMaps';

/**
 * Ledger entries — read-only journal of every account leg. Users post
 * via JournalEntry; each journal entry auto-generates the matching
 * ledger rows. The "+" button at the top routes the user to the
 * JournalEntry create page (which is what they actually need) plus
 * an explainer note so bookkeepers know why the action moved.
 *
 * Bug ref: AI Agent — "the '+' option is missing in Ledger Entries
 * while adding data".
 */
export default function AllLedgerEntries() {
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3">
        <p className="text-xs text-[var(--color-text-secondary)]">
          Ledger entries are auto-generated from journal entries. To
          post a new account leg, create a journal entry — the matching
          debit / credit rows appear here automatically.
        </p>
        <Link
          to="/panel/journal-entries/new"
          className="inline-flex h-9 items-center gap-1 rounded-md bg-[var(--color-primary)] px-3 text-xs font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]"
        >
          <Plus className="h-3.5 w-3.5" /> New journal entry
        </Link>
      </div>
      <CrudList<LedgerEntry, LedgerEntryCreate, LedgerEntryFilter>
        title="Ledger entries" newLabel="" service={ledgerEntryService}
        idKey="ledgerEntryId" labelKey="ledgerEntryId" basePath="/panel/ledger-entries"
        // Explicit `module` — inferModuleFromPath would drop only the
        // trailing "s" and produce "LedgerEntrie", missing the backend
        // module "LedgerEntry".
        module="LedgerEntry"
        // Newest ledger row on top — audit trail. Ref: ORDER_WORKFLOW_AUDIT #6.
        initialSortStatus={{ columnAccessor: 'entryDate', direction: 'desc' }}
      columns={[
        { accessor: 'entryDate', title: 'Date', sortable: true, render: (r) => r.entryDate ? new Date(r.entryDate).toLocaleDateString() : '—' },
        /* Resolve `accountId` GUID → account name via NameCell. Was
           rendering as a raw monospace GUID, which the user reported
           as "store / category / other dropdown list show value in
           review and click any page grid — it should show name". */
        { accessor: 'accountId', title: 'Account',
          render: (r) => <NameCell kind="account" id={r.accountId} /> },
        { accessor: 'debit', title: 'Debit', sortable: true, textAlign: 'right',
          render: (r) => Number(r.debit ?? 0) > 0 ? Number(r.debit).toLocaleString('en-IN') : '—' },
        { accessor: 'credit', title: 'Credit', sortable: true, textAlign: 'right',
          render: (r) => Number(r.credit ?? 0) > 0 ? Number(r.credit).toLocaleString('en-IN') : '—' },
        { accessor: 'runningBalance', title: 'Balance', sortable: true, textAlign: 'right',
          render: (r) => Number(r.runningBalance ?? 0).toLocaleString('en-IN') },
        { accessor: 'description', title: 'Description', render: (r) => r.description ?? '—' },
      ]}
      />
    </div>
  );
}
